import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Redux actions
import { getRolesFunc } from "../../../store/custom-actions/roles-actions/GetRolesSlicer";
import { addAdminFunc } from "../../../store/custom-actions/admins-actions/AddAdminSlicer";

// UI Components
import PageTitle from "../../layouts/PageTitle";
import FormField from "../../components/ui/FormField";
import SubmitButton from "../../components/ui/SubmitButton";

export default function NewAdminForm() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();

  // Redux states
  const { loading: getRolesLoading, data: rolesData = [] } = useSelector(
    (state) => state.getRolesSlicer
  );
  const { loading: addAdminLoading } = useSelector(
    (state) => state.addAdminSlicer
  );

  // Local state
  const [roleName, setRoleName] = useState("");
  const [adminImage, setAdminImage] = useState(null);
  const [imageViewer, setImageViewer] = useState("");

  // Fetch roles data
  useEffect(() => {
    dispatch(getRolesFunc());
  }, [dispatch]);

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setImageViewer("");
      setAdminImage(null);
      return;
    }

    try {
      const URLObject = window.URL || window.webkitURL || URL;
      if (URLObject && typeof URLObject.createObjectURL === "function") {
        const imageUrl = URLObject.createObjectURL(file);
        setImageViewer(imageUrl);
        setAdminImage(file);
      } else {
        console.error("URL.createObjectURL is not supported");
        setImageViewer("");
        setAdminImage(file);
      }
    } catch (error) {
      console.error("Error creating object URL:", error);
      setImageViewer("");
      setAdminImage(file);
    }
  };

  // Clean up object URL
  const cleanupObjectURL = () => {
    if (imageViewer) {
      try {
        const URLObject = window.URL || window.webkitURL || URL;
        if (URLObject && typeof URLObject.revokeObjectURL === "function") {
          URLObject.revokeObjectURL(imageViewer);
        }
      } catch (error) {
        console.error("Error revoking object URL:", error);
      }
    }
  };

  // Handle form submission
  const onSubmit = async (data) => {
    if (!data) return;

    const adminFormData = new FormData();
    adminFormData.append("name", data.name);
    adminFormData.append("phone", data.phone);
    adminFormData.append("email", data.email);
    adminFormData.append("password", data.password);
    adminFormData.append("role", roleName);
    adminFormData.append("password_confirmation", data.password_confirmation);

    if (adminImage) {
      adminFormData.append("image", adminImage);
    }

    const result = await dispatch(addAdminFunc({ data: adminFormData }));

    if (addAdminFunc.fulfilled.match(result)) {
      cleanupObjectURL();
      reset();
      setRoleName("");
      setImageViewer("");
      setAdminImage(null);
      setTimeout(() => navigate("/admins"), 2000);
    }
  };

  return (
    <>
      <PageTitle
        motherMenu={"Admins"}
        activeMenu={"New Admin"}
        motherMenuPath="/admins"
      />
      <div className="row">
        <div className="col-xl-12 col-xxl-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">{t("Create Admin")}</h5>
            </div>
            <div className="card-body">
              <form id="new-admin-form" onSubmit={handleSubmit(onSubmit)}>
                {/* Role Selection */}
                <div className="form-group mb-3">
                  <label>{t("Role")}</label>
                  {getRolesLoading ? (
                    <p>{t("Loading")}...</p>
                  ) : rolesData.length === 0 ? (
                    <p>{t("No data available")}</p>
                  ) : (
                    <select
                      className="form-control"
                      onChange={(e) => setRoleName(e.target.value)}
                      value={roleName}
                      required
                    >
                      <option value="" disabled>
                        {t("Choose Role")}...
                      </option>
                      {rolesData.map((role, index) => (
                        <option key={index} value={role.name}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Name and Email */}
                <div className="row">
                  <div className="col-sm-12 col-md-6">
                    <FormField
                      label={t("Name")}
                      name="name"
                      register={register}
                      required={true}
                      errors={errors}
                    />
                  </div>
                  <div className="col-sm-12 col-md-6">
                    <FormField
                      label={t("Email")}
                      name="email"
                      type="email"
                      register={register}
                      required={true}
                      errors={errors}
                    />
                  </div>
                </div>

                {/* Phone */}
                <div className="row">
                  <div className="col-sm-12">
                    <FormField
                      label={t("Phone")}
                      name="phone"
                      register={register}
                      required={true}
                      errors={errors}
                    />
                  </div>
                </div>

                {/* Password Fields */}
                <div className="row">
                  <div className="col-sm-12 col-md-6">
                    <FormField
                      label={t("Password")}
                      name="password"
                      type="password"
                      register={register}
                      required={true}
                      errors={errors}
                    />
                  </div>
                  <div className="col-sm-12 col-md-6">
                    <FormField
                      label={t("Confirm Password")}
                      name="password_confirmation"
                      type="password"
                      register={register}
                      required={true}
                      errors={errors}
                    />
                  </div>
                </div>

                {/* Image Upload */}
                {adminImage && imageViewer && (
                  <div className="form-group mb-3">
                    <div className="relative flex justify-center items-center mb-2">
                      <div className="relative w-[240px] h-[200px]">
                        <img
                          src={imageViewer}
                          alt="admin preview"
                          className="absolute top-0 left-0 w-full h-full object-cover rounded"
                        />
                      </div>
                    </div>
                  </div>
                )}
                <div className="form-group mb-3">
                  <label>{t("Admin Image")}</label>
                  <div className="w-full flex justify-center items-center mb-1">
                    <span className="text-sm">{t("Best size")} (200px * 180px)</span>
                  </div>
                  <label htmlFor="admin-image-file" className="file-upload-label">
                    <div className="file-upload-design">
                      <svg viewBox="0 0 640 512" height="1em">
                        <path d="M144 480C64.5 480 0 415.5 0 336c0-62.8 40.2-116.2 96.2-135.9c-.1-2.7-.2-5.4-.2-8.1c0-88.4 71.6-160 160-160c59.3 0 111 32.2 138.7 80.2C409.9 102 428.3 96 448 96c53 0 96 43 96 96c0 12.2-2.3 23.8-6.4 34.6C596 238.4 640 290.1 640 352c0 70.7-57.3 128-128 128H144zm79-217c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l39-39V392c0 13.3 10.7 24 24 24s24-10.7 24-24V257.9l39 39c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-80-80c-9.4-9.4-24.6-9.4-33.9 0l-80 80z"></path>
                      </svg>
                      <span className="browse-button">{t("Browse file")}</span>
                    </div>
                    <input
                      id="admin-image-file"
                      type="file"
                      accept="image/jpeg, image/png, image/jpg"
                      className="form-control"
                      onChange={handleFileChange}
                    />
                  </label>
                </div>

                {/* Submit Button */}
                <div className="text-end">
                  <SubmitButton
                    form="new-admin-form"
                    isLoading={addAdminLoading}
                    label="Submit"
                    loadingLabel={t("Loading")}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
